import {
  AppService,
  CommutingTripSummary,
  CommutingTripService,
  Employee,
  Event,
  Partner,
  TEmployeeTransportHabits,
  TPartnerTypeWithHolding,
  User,
  UserGeogroup,
  UserPlace,
  useCancellablePromise,
  frenchCountryCodes,
  TFrenchCountryCode,
} from '@geovelo-frontends/commons';
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import 'moment/locale/fr';
import { Font } from '@react-pdf/renderer';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SnackbarProvider } from 'notistack';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter } from 'react-router';

import NunitoBold from '../assets/fonts/NunitoSans_10pt_SemiCondensed-Black.ttf';
import NunitoItalic from '../assets/fonts/NunitoSans_7pt_SemiCondensed-Italic.ttf';
import NunitoRegular from '../assets/fonts/NunitoSans_7pt_SemiCondensed-Medium.ttf';
import { environment } from '../environment';
import usePartnerAvailableEventsQuery from '../hooks/use-partner-available-events-query';
import usePartnerEmployeesSummaryQuery from '../hooks/use-partner-employees-summary-query';
import usePartnerGeogroupQuery from '../hooks/use-partner-geogroup-query';
import usePartnerSubgroupsQuery from '../hooks/use-partner-subgroups-query';

import {
  AppContext,
  TGetStartedAdminProgression,
  TGetStartedAdminStepProgression,
  TGetStartedAdminSteps,
  TGetStartedAdminSubsteps,
  TGetStartedEmployeeSteps,
  TPermissionKey,
  getStartedAdminSteps,
  getStartedEmployeeSteps,
  offersMap,
} from './context';
import {
  getReactQueryDevtoolsVisibility,
  subscribeReactQueryDevtoolsVisibility,
} from './devtools-toggle';
import i18n from './i18n';
import Router, { TRouterRef } from './router';
import theme from './theme';
import './tracking';

Font.register({
  family: 'Nunito',
  fonts: [
    { src: NunitoRegular, fontWeight: 400 },
    { src: NunitoItalic, fontWeight: 400, fontStyle: 'italic' },
    { src: NunitoBold, fontWeight: 700 },
  ],
});

Font.registerEmojiSource({
  format: 'png',
  url: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/72x72/',
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 3 * 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: 1,
    },
  },
});

function App(): React.JSX.Element {
  const devtoolsVisible = useSyncExternalStore(
    subscribeReactQueryDevtoolsVisibility,
    getReactQueryDevtoolsVisibility,
    () => false,
  );

  return (
    <QueryClientProvider client={queryClient}>
      <AppInner />
      {devtoolsVisible && <ReactQueryDevtools initialIsOpen />}
    </QueryClientProvider>
  );
}

function AppInner(): React.JSX.Element {
  const history = useRef([]);
  const [initialized, setInitialized] = useState(false);
  const [currentEvent, setCurrentEvent] = useState<Event | null | undefined>(undefined);

  // user context
  const [currentUser, setCurrentUser] = useState<User | null>();

  const previousUserIdRef = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    const userId = currentUser?.id ?? null;
    if (previousUserIdRef.current !== undefined && previousUserIdRef.current !== userId) {
      queryClient.clear();
    }
    previousUserIdRef.current = userId;
  }, [currentUser]);

  const [userIsRegister, setUserIsRegister] = useState<boolean>();
  const [userGetStartedProgression, setUserGetStartedProgression] = useState<{
    allDone: boolean;
    doneCount: number;
    ignoredSteps: { [key in TGetStartedEmployeeSteps]?: boolean };
    stepsDone: { [key in TGetStartedEmployeeSteps]: boolean };
  }>();
  const [userEmployee, setUserEmployee] = useState<Employee | null>();
  const [userEmployees, setUserEmployees] =
    useState<Array<{ employee: Employee; partner: Partner }>>();
  const [userTeam, setUserTeam] = useState<UserGeogroup | null>();
  const [userSite, setUserSite] = useState<UserGeogroup | null>();
  const [userHome, setUserHome] = useState<UserPlace | null>();
  const [_userWorks, _setUserWorks] = useState<UserPlace[]>();
  const [userWorks, setUserWorks] = useState<UserPlace[]>();
  const [userCommutingTrips, setUserCommutingTrips] = useState<CommutingTripSummary[]>();
  const [userTransportHabits, setUserTransportHabits] = useState<TEmployeeTransportHabits | null>();
  const [userCreationReasonDialogSeen, setUserCreationReasonDialogSeen] = useState<boolean>();

  // partner context
  const [currentPartner, setCurrentPartner] = useState<Partner | null>();
  const [currentPartnerGetStartedProgression, setCurrentPartnerGetStartedProgression] =
    useState<TGetStartedAdminProgression>();
  const [currentPartnerType, setCurrentPartnerType] = useState<TPartnerTypeWithHolding | null>();
  const { data: currentPartnerGeogroup } = usePartnerGeogroupQuery(currentPartner);
  const { data: currentPartnerSubgroups } = usePartnerSubgroupsQuery(currentPartner, {
    isHolding: currentPartnerType === 'holding',
  });
  const { data: currentPartnerEmployeesSummary } = usePartnerEmployeesSummaryQuery(
    currentPartner,
    userEmployee,
    { enabled: currentPartnerType !== 'holding' },
  );
  const [currentPartnerPermissions, setCurrentPartnerPermissions] = useState<{
    [key in TPermissionKey]: boolean;
  }>(offersMap.free.permissions);
  // Fetched for admins/animators on a non-holding partner, skipped otherwise (every real consumer
  // re-checks the role anyway before using this data, e.g. pages/challenges/index.tsx's "Add" CTA).
  const { data: currentPartnerAvailableEventsData } = usePartnerAvailableEventsQuery(
    currentPartner,
    currentPartnerGeogroup,
    { enabled: currentPartnerType !== 'holding' && !!userEmployee?.isAdminOrAnimator },
  );
  const [currentPartnerEventDialogOpen, openCurrentPartnerEventDialog] = useState(false);
  const [isFrenchGeogroup, setIsFrenchGeogroup] = useState<boolean>();
  const [currentPartnerMobilitySurveyDialogOpen, openCurrentPartnerMobilitySurveyDialog] =
    useState(false);
  const firstMembersAddedDialogOpenRef = useRef<boolean>(false);

  const routerRef = useRef<TRouterRef>(null);
  const { cancellablePromise, cancelPromises } = useCancellablePromise();

  useEffect(() => {
    AppService.environment = environment;
    setInitialized(true);
  }, []);

  useEffect(() => {
    if (!initialized) return;

    if (currentPartnerType !== 'holding' && userEmployee && _userWorks) {
      getUserCommutingTrips(userEmployee.id);
    }

    return () => {
      setUserCommutingTrips(undefined);
    };
  }, [initialized, userEmployee, _userWorks]);

  useEffect(() => {
    filterWorks();
  }, [userCommutingTrips, _userWorks]);

  useEffect(() => {
    if (
      userEmployee?.isAdminOrAnimator &&
      currentPartner &&
      currentPartnerType &&
      currentPartnerType !== 'holding' &&
      currentPartnerEmployeesSummary !== undefined &&
      currentPartnerAvailableEventsData !== undefined
    ) {
      const steps: {
        [key in TGetStartedAdminSteps]: TGetStartedAdminStepProgression<
          TGetStartedAdminSubsteps[key]
        >;
      } = {
        accountConfig: { doneCount: 0, allDone: false, substeps: [] },
        firstChallenge: { doneCount: 0, allDone: false, substeps: [] },
        discoverOffers: { doneCount: 0, allDone: false, substeps: [] },
      };

      getStartedAdminSteps.forEach((key) => {
        switch (key) {
          case 'accountConfig': {
            const hasLogo =
              Boolean(currentPartner.icon) ||
              Boolean(userEmployee.surveyAnswers.getStartedAdminSkippedSteps_logo);
            const hasEmployees =
              currentPartnerEmployeesSummary.employeesCount > 1 ||
              Boolean(userEmployee.surveyAnswers.getStartedAdminSkippedSteps_invitations);

            steps.accountConfig = {
              doneCount: (hasLogo ? 1 : 0) + (hasEmployees ? 1 : 0),
              allDone: hasLogo && hasEmployees,
              substeps: [
                { key: 'logo', done: hasLogo },
                { key: 'invitations', done: hasEmployees },
              ],
            };

            break;
          }
          case 'firstChallenge': {
            const hasChallenges =
              currentPartnerAvailableEventsData.hasChallenges ||
              Boolean(userEmployee.surveyAnswers.getStartedAdminSkippedSteps_firstChallenge);

            steps.firstChallenge = {
              doneCount: hasChallenges ? 1 : 0,
              allDone: hasChallenges,
              substeps: [{ key: 'firstChallenge', done: hasChallenges }],
            };

            break;
          }
          case 'discoverOffers': {
            const done =
              currentPartnerType !== 'free' ||
              Boolean(userEmployee.surveyAnswers.getStartedAdminDoneSteps_discoverOffers);
            steps.discoverOffers = {
              doneCount: done ? 1 : 0,
              allDone: done,
              substeps: [{ key: 'discoverOffers', done }],
            };

            break;
          }
          default:
            break;
        }
      });

      const doneCount = getStartedAdminSteps.filter((key) => steps[key].allDone).length;
      const allDone = getStartedAdminSteps.every((key) => steps[key].allDone);

      setCurrentPartnerGetStartedProgression({
        doneCount,
        allDone,
        steps,
      });
    }

    return () => {
      setCurrentPartnerGetStartedProgression(undefined);
    };
  }, [
    userEmployee,
    currentPartner,
    currentPartnerType,
    currentPartnerEmployeesSummary,
    currentPartnerAvailableEventsData,
  ]);

  useEffect(() => {
    const currentPartnerTeams = currentPartnerSubgroups?.teams;

    if (
      userEmployee &&
      currentPartnerTeams !== undefined &&
      currentUser &&
      userEmployee &&
      userTeam !== undefined &&
      userCommutingTrips !== undefined
    ) {
      const ignoredSteps = getStartedEmployeeSteps.reduce<{
        [key in TGetStartedEmployeeSteps]?: boolean;
      }>((res, key) => {
        res[key] =
          key === 'team' && (!currentPartnerTeams || currentPartnerTeams.length === 0)
            ? true
            : Boolean(userEmployee.surveyAnswers[`getStartedEmployeeIgnoredSteps_${key}`]);

        return res;
      }, {});

      const stepsDone: { [key in TGetStartedEmployeeSteps]: boolean } = {
        homeWorkJourney: false,
        profilePicture: false,
        team: false,
      };

      getStartedEmployeeSteps.forEach((key) => {
        switch (key) {
          case 'homeWorkJourney':
            if (userCommutingTrips.length > 0) {
              stepsDone.homeWorkJourney = true;
            }
            break;
          case 'profilePicture':
            if (currentUser.profilePicture) {
              stepsDone.profilePicture = true;
            }
            break;
          case 'team':
            if (userTeam) {
              stepsDone.team = true;
            }
            break;
          default:
            break;
        }
      });

      const doneCount = getStartedEmployeeSteps.filter(
        (key) => stepsDone[key] && !ignoredSteps[key],
      ).length;
      const allDone = getStartedEmployeeSteps.every((key) => stepsDone[key] || ignoredSteps[key]);

      setUserGetStartedProgression({ stepsDone, ignoredSteps, doneCount, allDone });
    }

    return () => {
      setUserGetStartedProgression(undefined);
    };
  }, [currentPartnerSubgroups, currentUser, userEmployee, userWorks, userTeam, userCommutingTrips]);

  useEffect(() => {
    if (currentPartnerGeogroup) {
      setIsFrenchGeogroup(
        currentPartnerGeogroup?.countryCode
          ? frenchCountryCodes.includes(
              currentPartnerGeogroup.countryCode.toLowerCase() as TFrenchCountryCode,
            )
          : undefined,
      );
    }
  }, [currentPartnerGeogroup]);

  async function getUserCommutingTrips(employeeId: number) {
    try {
      const trips = await cancellablePromise(
        CommutingTripService.getCommutingTrips({ employeeId }),
      );

      setUserCommutingTrips(trips);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        console.error(err);
        setUserCommutingTrips([]);
      }
    }
  }

  async function filterWorks() {
    if (_userWorks !== undefined && userCommutingTrips !== undefined) {
      const ids = userCommutingTrips.reduce<number[]>((res, { work: { id } }) => {
        if (typeof id === 'number') res.push(id);
        return res;
      }, []);

      const filteredWorks = _userWorks.filter(
        ({ id }) => typeof id === 'number' && ids.includes(id),
      );

      setUserWorks(filteredWorks);
    } else {
      setUserWorks(undefined);
    }
  }

  if (!initialized) return <></>;

  return (
    <AppContext.Provider
      value={{
        history,
        app: {
          currentEvent,
          pageVisibility: {},
        },
        user: {
          current: currentUser,
          isRegister: userIsRegister,
          getStartedProgression: userGetStartedProgression,
          employee: userEmployee,
          employees: userEmployees,
          team: userTeam,
          site: userSite,
          home: userHome,
          works: userWorks,
          commutingTrips: userCommutingTrips,
          transportHabits: userTransportHabits,
          creationReasonDialogSeen: userCreationReasonDialogSeen,
        },
        partner: {
          current: currentPartner,
          getStartedProgression: currentPartnerGetStartedProgression,
          type: currentPartnerType,
          isFrenchGeogroup,
          permissions: currentPartnerPermissions,
          eventDialogOpen: currentPartnerEventDialogOpen,
          mobilitySurveyDialogOpen: currentPartnerMobilitySurveyDialogOpen,
          firstMembersAddedDialogOpenRef,
        },
        actions: {
          setCurrentEvent,
          setCurrentUser,
          selectCompany: (employee, partner) => routerRef.current?.selectCompany(employee, partner),
          setCurrentPartner,
          setPartnerType: setCurrentPartnerType,
          setIsFrenchGeogroup,
          setPartnerPermissions: setCurrentPartnerPermissions,
          openEventDialog: openCurrentPartnerEventDialog,
          openMobilitySurveyDialog: openCurrentPartnerMobilitySurveyDialog,
          setUserIsRegister,
          setUserEmployee,
          setUserEmployees,
          setUserTeam,
          setUserSite,
          setUserHome,
          setUserWorks: _setUserWorks,
          setUserCommutingTrips,
          setUserTransportHabits,
          setUserCreationReasonDialogSeen,
        },
      }}
    >
      <I18nextProvider i18n={i18n}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <SnackbarProvider
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            maxSnack={3}
          >
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <BrowserRouter>
                <Router
                  cancellablePromise={cancellablePromise}
                  cancelPromises={cancelPromises}
                  ref={routerRef}
                  userWorks={_userWorks}
                />
              </BrowserRouter>
            </LocalizationProvider>
          </SnackbarProvider>
        </ThemeProvider>
      </I18nextProvider>
    </AppContext.Provider>
  );
}

export default App;
